import { defineStore } from 'pinia'
import { useTeamStore, useSystemStore } from '@/stores'
import { configureLogging } from '@/modules/analytics'
import { setAppStateListener } from '@/modules/native'
import {
  getTeamsOfUser,
  getUser,
  setMemberProperties
} from '@/modules/database'
import { Timestamp } from '@/modules/datetime'
import { EventVisibility, roleHierarchy } from '@/modules/config'
import type { User as FirebaseUser } from 'firebase/auth'

export const useUserStore = defineStore('user', {
  state: () => ({
    auth: {
      user: null as FirebaseUser | null
    },
    userData: null as User | null
  }),
  getters: {
    hasRole() {
      return (role: TeamRole, exact = false) => {
        const userRole = this.userRole

        if (exact) {
          return role === userRole
        } else {
          return roleHierarchy.indexOf(role) <= roleHierarchy.indexOf(userRole)
        }
      }
    },
    /**
     * Returns whether the current user is an admin for the active team.
     */
    userIsAdmin: (state) => {
      const teamStore = useTeamStore()
      return teamStore.activeTeam.adminIds.includes(state.auth.user?.uid ?? '')
    },
    /**
     * Returns whether the current user is an admin for the active team.
     */
    userIsCoach: (state) => {
      const teamStore = useTeamStore()
      return teamStore.activeTeam.coachIds.includes(state.auth.user?.uid ?? '')
    },
    userRole: (state): TeamRole => {
      const teamStore = useTeamStore()
      const userId = state.auth.user?.uid ?? ''

      if (teamStore.activeTeam.adminIds.includes(userId)) {
        return 'admin'
      } else if (teamStore.activeTeam.coachIds.includes(userId)) {
        return 'coach'
      } else if (teamStore.activeTeam.memberIds.includes(userId)) {
        return 'member'
      }

      return 'visitor'
    },
    userEventVisibilityValue(): EventVisibilityValue {
      const role = this.userRole

      switch (role) {
        case 'admin':
          return EventVisibility.Admin
        case 'coach':
          return EventVisibility.Coach
        case 'member':
          return EventVisibility.Member
        default:
          return EventVisibility.Public
      }
    },
    userCanCreateEvents(): boolean {
      const teamStore = useTeamStore()
      return (
        this.userIsAdmin ||
        (this.hasRole('member') &&
          teamStore.activeTeam.allowMemberEvents !== false)
      )
    }
  },
  actions: {
    async initialiseUser(userId: string) {
      const teamStore = useTeamStore()

      if (!teamStore.activeTeam.id) {
        const userTeams = await getTeamsOfUser(userId)
        teamStore.teams = userTeams
        if (userTeams.length) {
          teamStore.activeTeam = userTeams[0]
        }
      }

      if (!this.userData) {
        const systemStore = useSystemStore()

        this.userData = await getUser(userId)

        configureLogging({
          payload: {
            person: {
              id: this.userData.id,
              username: `${this.userData.firstName} ${this.userData.lastName}`,
              email: this.userData.email
            }
          }
        })

        const lastSelectedTeam = this.userData.preferences.team?.lastSelected
        if (lastSelectedTeam) {
          const team = teamStore.teams.find(
            (team) => team.id === lastSelectedTeam
          )
          if (team) {
            teamStore.activeTeam = team
          }
        }

        // Notifications are opt-in by default
        const isPushOptedIn = Boolean(
          this.userData?.preferences?.notifications?.enabled
        )
        const hasPermission =
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        systemStore.notifications.isGranted = isPushOptedIn && hasPermission

        if (!systemStore.isDev && isPushOptedIn && hasPermission) {
          try {
            await systemStore.subscribeMessaging(userId)
          } catch (error) {
            console.error('Error subscribing to messaging:', error)
          }
        }

        setAppStateListener(({ isActive }) => {
          if (isActive && this.userData && teamStore.activeTeam.id) {
            // For the sake of simplicity, we use the client's clock here instead of the server's
            setMemberProperties(teamStore.activeTeam.id, this.userData.id, {
              lastActive: Timestamp.now()
            })
          }
        })
      }
    }
  }
})
